import { RideSolvedResponseRideSolvedWaypoint } from "../../../../../common/api/raily/ride/solved/ride-solved.response";
import {
  OrderJoinCargoOrderRide,
  OrderJoinPreviewRideWaypoint,
  OrderJoinPreviewRideWaypointGroup,
  OrderJoinSolvedOrderRideSeqItem,
  OrderJoinTransportingOrder,
} from "../../../../../common/services/order/join/order-join";
import { SearchRoadRoutingResponseRouteLeg } from "../../../../../common/utils/search-road-route/search-road-routing.response";
import orderJoinHelper from "../../order-join.helper";
import OrderJoinOrderDetailsRouteWaypoint from "../types/order-join-order-details-route-waypoint";

const createOrderDetailsWaypointForSolvedOrderRide = (
  responseRideWaypoint: OrderJoinSolvedOrderRideSeqItem,
  stageNo: number,
  distance: number | null,
  completionDate: Date | null,
): OrderJoinOrderDetailsRouteWaypoint => {
  return {
    haltingTime: responseRideWaypoint.haltingTime,
    onboardingPassengersUuids:
      responseRideWaypoint.meta.cargoEnter.subjects.map(
        (subject) => subject.id,
      ),
    outboardingPassengersUuids:
      responseRideWaypoint.meta.cargoExit.subjects.map((subject) => subject.id),
    orderedDate: responseRideWaypoint.time
      ? new Date(responseRideWaypoint.time)
      : null,
    place: {
      displayName: responseRideWaypoint.displayName,
      latitude: responseRideWaypoint.lat,
      longitude: responseRideWaypoint.lon,
    },
    stageNo,
    completionDate,
    distance,
    estimatedDate: new Date(responseRideWaypoint.meta.estimatedTime),
  };
};

const createOrderDetailsWaypointsFromSolvedOrders = (
  responseSolvedOrderRideSeqItems: OrderJoinSolvedOrderRideSeqItem[],
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
): OrderJoinOrderDetailsRouteWaypoint[] => {
  let waypoints: OrderJoinOrderDetailsRouteWaypoint[] = [];

  let stageNo = 1;

  responseSolvedOrderRideSeqItems.forEach((sequenceItem) => {
    const routeFragmentDetails = responseRouteLegs.find((responseRouteLeg) => {
      return (
        responseRouteLeg.end.latitude === sequenceItem.lat &&
        responseRouteLeg.end.longitude === sequenceItem.lon
      );
    });

    const distance = responseRouteLegs.length
      ? (routeFragmentDetails?.distance ?? 0)
      : null;

    const completionDate = null;

    const newWaypoint = createOrderDetailsWaypointForSolvedOrderRide(
      sequenceItem,
      stageNo,
      distance,
      completionDate,
    );

    waypoints = [...waypoints, newWaypoint];

    stageNo++;
  });

  return waypoints;
};

const createOrderWaypointFromCargoOrderRide = (
  responseRideWaypoint: OrderJoinPreviewRideWaypoint,
  stageNo: number,
  distance: number | null,
  completionDate: Date | null,
  estimatedDate: Date | null,
): OrderJoinOrderDetailsRouteWaypoint => {
  return {
    haltingTime: responseRideWaypoint.haltingTime,
    onboardingPassengersUuids:
      responseRideWaypoint.meta.cargoEnter.subjects.map(
        (subject) => subject.id,
      ),
    outboardingPassengersUuids:
      responseRideWaypoint.meta.cargoExit.subjects.map((subject) => subject.id),
    orderedDate: responseRideWaypoint.time
      ? new Date(responseRideWaypoint.time)
      : null,
    place: {
      displayName: responseRideWaypoint.displayName,
      latitude: responseRideWaypoint.lat,
      longitude: responseRideWaypoint.lon,
    },
    stageNo,
    completionDate,
    distance,
    estimatedDate,
  };
};

const createOrderWaypointFromCargoOrderRideGroup = (
  responseRideWaypointGroup: OrderJoinPreviewRideWaypointGroup,
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
  responseCargoOrderSolvedRides: RideSolvedResponseRideSolvedWaypoint[],
  isGroup?: boolean,
): OrderJoinOrderDetailsRouteWaypoint[] => {
  let waypoints: OrderJoinOrderDetailsRouteWaypoint[] = [];
  let stageNo = 1;

  responseRideWaypointGroup.seq.forEach((sequenceItem) => {
    const routeFragmentDetails = responseRouteLegs.find(
      (responseRouteLeg) =>
        responseRouteLeg.end.latitude === sequenceItem.lat &&
        responseRouteLeg.end.longitude === sequenceItem.lon,
    );

    const distance = responseRouteLegs.length
      ? (routeFragmentDetails?.distance ?? 0)
      : null;

    const completionDate = null;

    const foundCargoOrderSolvedRide = responseCargoOrderSolvedRides.find(
      (ride) => ride.meta.id === sequenceItem.meta.id,
    );

    const estimatedDate =
      !isGroup && foundCargoOrderSolvedRide?.meta.estimatedTime
        ? new Date(foundCargoOrderSolvedRide.meta.estimatedTime)
        : null;

    const newWaypoint = createOrderWaypointFromCargoOrderRide(
      sequenceItem,
      stageNo,
      distance,
      completionDate,
      estimatedDate,
    );

    waypoints = [...waypoints, newWaypoint];
    stageNo++;
  });

  return waypoints;
};

const createOrderWaypointsFromCargoOrderRide = (
  responseCargoOrderRide: OrderJoinCargoOrderRide,
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
  responseCargoOrderSolvedRides: RideSolvedResponseRideSolvedWaypoint[],
  isGroup?: boolean,
): OrderJoinOrderDetailsRouteWaypoint[] => {
  let waypoints: OrderJoinOrderDetailsRouteWaypoint[] = [];
  let stageNo = 1;

  responseCargoOrderRide.seq.forEach((sequenceItem) => {
    const isRideWaypoint =
      orderJoinHelper.isResponseRideSeqItemAWaypoint(sequenceItem);

    if (isRideWaypoint) {
      const routeFragmentDetails = responseRouteLegs.find(
        (responseRouteLeg) =>
          responseRouteLeg.end.latitude === sequenceItem.lat &&
          responseRouteLeg.end.longitude === sequenceItem.lon,
      );

      const distance = responseRouteLegs.length
        ? (routeFragmentDetails?.distance ?? 0)
        : null;

      const completionDate = null;

      const foundCargoOrderSolvedRide = responseCargoOrderSolvedRides.find(
        (ride) => ride.meta.id === sequenceItem.meta.id,
      );

      const estimatedDate =
        !isGroup && foundCargoOrderSolvedRide?.meta.estimatedTime
          ? new Date(foundCargoOrderSolvedRide.meta.estimatedTime)
          : null;

      const newWaypoint = createOrderWaypointFromCargoOrderRide(
        sequenceItem,
        stageNo,
        distance,
        completionDate,
        estimatedDate,
      );

      waypoints.push(newWaypoint);

      stageNo++;
    } else {
      const newWaypoints = createOrderWaypointFromCargoOrderRideGroup(
        sequenceItem as OrderJoinPreviewRideWaypointGroup,
        responseRouteLegs,
        responseCargoOrderSolvedRides,
        true,
      );

      newWaypoints.forEach((waypoint) => (waypoint.stageNo = stageNo));

      waypoints = [...waypoints, ...newWaypoints];

      stageNo++;
    }
  });

  return waypoints;
};

const createWaypointsForOrderedRoute = (
  responseCargoOrderRide: OrderJoinCargoOrderRide,
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
  responseCargoOrderSolvedRides: RideSolvedResponseRideSolvedWaypoint[],
): OrderJoinOrderDetailsRouteWaypoint[] => {
  return createOrderWaypointsFromCargoOrderRide(
    responseCargoOrderRide,
    responseRouteLegs,
    responseCargoOrderSolvedRides,
  );
};

const createWaypointsForSolvedRoute = (
  responseTransportingOrders: OrderJoinTransportingOrder[],
  responseRouteLegs: SearchRoadRoutingResponseRouteLeg[],
): OrderJoinOrderDetailsRouteWaypoint[] => {
  const solvedOrderRidesFromAllTransportingOrders = responseTransportingOrders
    .map((item) => item.solvedOrder!)
    .map((item) => item.ride.seq)
    .flat();

  const waypoints = createOrderDetailsWaypointsFromSolvedOrders(
    solvedOrderRidesFromAllTransportingOrders,
    responseRouteLegs,
  );

  return waypoints;
};

const orderJoinOrderDetailsWaypointFactory = {
  createWaypointsForOrderedRoute,
  createWaypointsForSolvedRoute,
};

export default orderJoinOrderDetailsWaypointFactory;
